import React from 'react';
import { Printer, Download, Monitor, Settings } from 'lucide-react';

const PrinterSetupStyles = () => (
  <style>{`
    .printer-setup-page {
      max-width: 860px;
    }

    .printer-setup-header {
      margin-bottom: 32px;
    }
    .printer-setup-header h1 {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--saas-text-primary);
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0 0 8px 0;
    }
    .printer-setup-header p {
      font-size: 0.9rem;
      color: var(--saas-text-secondary);
      margin: 0;
    }

    .download-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 20px;
      margin-bottom: 36px;
    }

    .download-card {
      background: var(--saas-bg-surface);
      border: 1px solid var(--saas-border-color);
      border-radius: 12px;
      padding: 28px 24px;
      box-shadow: var(--saas-card-shadow);
      display: flex;
      flex-direction: column;
      gap: 16px;
      transition: all 0.2s;
    }
    .download-card:hover {
      box-shadow: 0 8px 24px rgba(0,0,0,0.08);
      transform: translateY(-2px);
    }

    .download-card-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: #d1fae5;
      color: #059669;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .download-card-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: var(--saas-text-primary);
      margin: 0;
    }
    .download-card-desc {
      font-size: 0.85rem;
      color: var(--saas-text-secondary);
      margin: 0;
      line-height: 1.5;
    }

    .download-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 20px;
      background: var(--saas-primary);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
      text-decoration: none;
    }
    .download-btn:hover {
      background: var(--saas-primary-hover);
    }

    .setup-section {
      background: var(--saas-bg-surface);
      border: 1px solid var(--saas-border-color);
      border-radius: 12px;
      padding: 28px 24px;
      box-shadow: var(--saas-card-shadow);
    }
    .setup-section h2 {
      font-size: 1.1rem;
      font-weight: 700;
      color: var(--saas-text-primary);
      margin: 0 0 20px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .setup-steps {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .setup-step {
      display: flex;
      gap: 14px;
      align-items: flex-start;
    }
    .step-number {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--saas-primary);
      color: white;
      font-size: 0.85rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .step-content h3 {
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--saas-text-primary);
      margin: 0 0 4px 0;
      line-height: 32px;
    }
    .step-content p {
      font-size: 0.85rem;
      color: var(--saas-text-secondary);
      margin: 0;
      line-height: 1.5;
    }

    @media (max-width: 768px) {
      .download-grid {
        grid-template-columns: 1fr;
      }
    }
  `}</style>
);

const AdminPrinterSetup = () => {
  const downloads = [
    {
      title: 'Windows x64 版本',
      desc: '適用於 64 位元 Windows 系統（大多數現代電腦）',
      url: 'https://drive.google.com/uc?export=download&id=1hg45Ieai80SyYPwpmSThwhQtPdSweqmy',
    },
  ];

  return (
    <>
      <PrinterSetupStyles />
      <div className="printer-setup-page">
        <div className="printer-setup-header">
          <h1><Printer size={24} /> 打印機管理</h1>
          <p>下載並安裝 Print Agent 桌面程式，自動接收並列印訂單。</p>
        </div>

        <div className="download-grid">
          {downloads.map((item) => (
            <div key={item.url} className="download-card">
              <div className="download-card-icon">
                <Monitor size={24} />
              </div>
              <h3 className="download-card-title">{item.title}</h3>
              <p className="download-card-desc">{item.desc}</p>
              <a
                className="download-btn"
                href={item.url}
                download
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download size={18} />
                下載 Print Agent
              </a>
            </div>
          ))}
        </div>

        <div className="setup-section">
          <h2><Settings size={18} /> 安裝教學</h2>
          <ol className="setup-steps">
            <li className="setup-step">
              <span className="step-number">1</span>
              <div className="step-content">
                <h3>解壓縮 ZIP 檔案</h3>
                <p>下載完成後，將 ZIP 檔案解壓縮到任意資料夾。</p>
              </div>
            </li>
            <li className="setup-step">
              <span className="step-number">2</span>
              <div className="step-content">
                <h3>執行 Print Agent</h3>
                <p>開啟解壓縮後的資料夾，雙擊執行 <strong>print-agent.exe</strong> 啟動程式。</p>
              </div>
            </li>
            <li className="setup-step">
              <span className="step-number">3</span>
              <div className="step-content">
                <h3>設定 Supabase 連線</h3>
                <p>在 Print Agent 中輸入 Supabase URL 與 Anon Key，連線成功後即可自動接收新訂單並列印。</p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </>
  );
};

export default AdminPrinterSetup;
